import { useFlags } from 'launchdarkly-react-client-sdk';
import { trpc } from '../../../utils/trpc';

export const AUTOFULFILL_NUDGE_ELIGIBLE_REQUEST_THRESHOLD = 1200;

/**
 * Returns whether the tenant meets the eligible request threshold for autofulfill nudges.
 * Reads LD flag 'hideAutofulfillNudgeBelowThreshold'. When the flag is true, returns false
 * for tenants with fewer than 1200 eligible requests.
 *
 * The count is only fetched when the flag is on, since the threshold is not consulted otherwise.
 */
export const useAutofulfillNudgeThreshold = (): boolean => {
    const { hideAutofulfillNudgeBelowThreshold } = useFlags();
    const { data: eligibleRequests } = trpc.requestManager.rmRouter.getDigitalEligibleRequestCounts.useQuery(
        undefined,
        {
            enabled: !!hideAutofulfillNudgeBelowThreshold,
        }
    );
    if (!hideAutofulfillNudgeBelowThreshold) return true;
    return (eligibleRequests?.eligible ?? 0) >= AUTOFULFILL_NUDGE_ELIGIBLE_REQUEST_THRESHOLD;
};

/**
 * Hook combining EMR/live-status eligibility with the eligible request threshold check.
 * Use this anywhere an autofulfill nudge is shown to the user.
 */
export const useAutofulfillNudge = (
    emr: string | null | undefined,
    eligibility: boolean | null | undefined,
    isLiveDigitalFulfillment: boolean | null | undefined
): boolean => {
    const passesThreshold = useAutofulfillNudgeThreshold();
    const passesBaseCheck =
        (emr === null || emr === undefined || (emr !== null && eligibility === true)) &&
        (isLiveDigitalFulfillment === false ||
            isLiveDigitalFulfillment === null ||
            isLiveDigitalFulfillment === undefined);
    return passesBaseCheck && passesThreshold;
};

export const base64ToObjectURL = (base64Image: string) => {
    // Split into two parts
    const parts = base64Image.split(';base64,');

    // Hold the content type
    const contentType = parts[0].split(':')[1];

    // Decode Base64 string
    const decodedData = window.atob(parts[1]);

    // Create UNIT8ARRAY of size same as row data length
    const uInt8Array = new Uint8Array(decodedData.length);

    // Insert all character code into uInt8Array
    for (let i = 0; i < decodedData.length; ++i) {
        uInt8Array[i] = decodedData.charCodeAt(i);
    }

    // Return BLOB image after conversion
    const blob = new Blob([uInt8Array], { type: contentType });

    return URL.createObjectURL(blob);
};
